import React, { ReactNode, createContext, useContext, useState } from 'react';
import { ApiResources } from '@services/utils';
import { Next } from 'types/proveIdentity';

interface ProveContextType {
  authToken: string | null;
  setAuthToken: (token: string) => void;
  challengeMissing: boolean;
  setChallengeMissing: (missing: boolean) => void;
  correlationId: string | null;
  setCorrelationId: (correlationId: string) => void;
  nextStep: Partial<Next> | null;
  setNextStep: (nextStep: Partial<Next>) => void;
  getNextEndpoint: (nextEndPoint: string) => ApiResources | undefined;
}

const ProveContext = createContext<ProveContextType | undefined>(undefined);

export const ProveProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [challengeMissing, setChallengeMissing] = useState<boolean>(false);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [nextStep, setNextStep] = useState<Partial<Next> | null>(null);

  function getNextEndpoint(nextEndPoint: string): ApiResources | undefined {
    const endPointApiResource: Record<string, ApiResources> = {
      'v3-validate': ApiResources.proveValidate,
      'v3-challenge': ApiResources.proveChallenge,
      'v3-complete': ApiResources.proveComplete,
    };

    return endPointApiResource[nextEndPoint as string];
  }

  return (
    <ProveContext.Provider
      value={{
        authToken,
        setAuthToken,
        challengeMissing,
        setChallengeMissing,
        correlationId,
        setCorrelationId,
        nextStep,
        setNextStep,
        getNextEndpoint,
      }}
    >
      {children}
    </ProveContext.Provider>
  );
};

export const useProve = () => {
  const context = useContext(ProveContext);
  if (!context) {
    throw new Error('useProve must be used within an ProveProvider');
  }
  return context;
};
